import axios from 'axios';
import qs from 'qs';

// Create axios instance pointing to main API (not manager API)
const mainAPI = axios.create({
    baseURL: URL_API
});

mainAPI.interceptors.request.use(async (request) => {
    if (localStorage.getItem('token')) {
        request.headers.Authorization = localStorage.getItem('token');
    }
    return request;
}, error => Promise.reject(error));

mainAPI.interceptors.response.use(response => response, error => Promise.reject(error));

// Course-level endpoints (creating/managing performance plan courses)
const saveUpdatePerformancePlan = (data) => mainAPI.post('course/saveUpdatePerformancePlan', qs.stringify(data));

// Configuration endpoints
const getConfig = ({ id_course }) => mainAPI.post('performancePlan/getConfig', qs.stringify({ id_course }));
const saveConfig = (data) => mainAPI.post('performancePlan/saveConfig', qs.stringify(data));

// Plan instance endpoints
const createPlan = (data) => mainAPI.post('performancePlan/create', qs.stringify(data));
const getPlan = ({ id_plan }) => mainAPI.post('performancePlan/get', qs.stringify({ id_plan }));
// expected_updated_at is the updated_at the client last saw. The backend
// rejects the write with 409 if the stored value has moved on, which stops one
// party's whole-blob autosave from silently overwriting the other's work.
const savePlan = ({ id_plan, form_data, expected_updated_at }) => mainAPI.post('performancePlan/save', qs.stringify({
    id_plan,
    form_data: typeof form_data === 'string' ? form_data : JSON.stringify(form_data),
    expected_updated_at: expected_updated_at || ''
}));

// History endpoint
const getHistory = ({ id_plan, limit, offset, field_path }) => mainAPI.post('performancePlan/history', qs.stringify({
    id_plan,
    limit: limit || 50,
    offset: offset || 0,
    field_path: field_path || null
}));

// Signature endpoint
const sign = ({ id_plan, stage, signature_data }) => mainAPI.post('performancePlan/sign', qs.stringify({
    id_plan,
    stage,
    signature_data: signature_data || null
}));

// Stage submission endpoint
const submitStage = ({ id_plan, stage }) => mainAPI.post('performancePlan/submitStage', qs.stringify({
    id_plan,
    stage
}));

// List endpoints
const myPlans = ({ plan_year, id_config, id_course }) => mainAPI.post('performancePlan/myPlans', qs.stringify({
    plan_year: plan_year || null,
    id_config: id_config || null,
    id_course: id_course || null
}));

// Self-service plan creation (employee starts their own plan)
const startPlan = ({ id_course, plan_year }) => mainAPI.post('performancePlan/startPlan', qs.stringify({
    id_course,
    plan_year: plan_year || new Date().getFullYear()
}));

const supervisorPlans = ({ plan_year, current_stage }) => mainAPI.post('performancePlan/supervisorPlans', qs.stringify({
    plan_year: plan_year || null,
    current_stage: current_stage || null
}));

// Direct reports who have not started a plan for the period yet.
const teamWithoutPlans = ({ id_course, plan_year }) => mainAPI.post('performancePlan/teamWithoutPlans', qs.stringify({
    id_course,
    plan_year: plan_year || null
}));

// Remind one of those reports to start.
const nudgeToStart = ({ id_user, id_course, plan_year }) => mainAPI.post('performancePlan/nudgeToStart', qs.stringify({
    id_user,
    id_course,
    plan_year: plan_year || null
}));

const listPlans = ({ id_course, plan_year, current_stage, page, perPage }) => mainAPI.post('performancePlan/listPlans', qs.stringify({
    id_course,
    plan_year: plan_year || null,
    current_stage: current_stage || null,
    page: page || 1,
    perPage: perPage || 20
}));

// Supervisor update endpoint
const updateSupervisor = ({ id_plan, id_supervisor }) => mainAPI.post('performancePlan/updateSupervisor', qs.stringify({
    id_plan,
    id_supervisor
}));

// Resolve supervisor email to user account
const resolveSupervisorEmail = ({ email }) => mainAPI.post('performancePlan/resolveSupervisorEmail', qs.stringify({
    email
}));

// Confirm supervisor assignment (by employee).
//
// `role` must be forwarded. It was previously dropped here, so "Add Additional
// Supervisor" reached the server without it, hit the server's `|| 'PRIMARY'`
// default, and demoted the plan's real supervisor to ADDITIONAL while
// repointing lms_performance_plan.id_supervisor at the new person — silently
// rewriting who is allowed to sign the plan.
const confirmSupervisor = ({ id_plan, id_supervisor, source, role }) => mainAPI.post('performancePlan/confirmSupervisor', qs.stringify({
    id_plan,
    id_supervisor,
    source: source || 'MANUAL',
    role: role || 'PRIMARY'
}));

// Search users for supervisor selection
const searchUsers = ({ search, limit }) => mainAPI.post('performancePlan/searchUsers', qs.stringify({
    search,
    limit: limit || 20
}));

// Request supervisor input (sends notification)
const requestSupervisorInput = ({ id_plan }) => mainAPI.post('performancePlan/requestSupervisorInput', qs.stringify({
    id_plan
}));

// Remove a supervisor from a plan
const removeSupervisor = ({ id_plan, id_supervisor }) => mainAPI.post('performancePlan/removeSupervisor', qs.stringify({
    id_plan,
    id_supervisor
}));

// PPP Form Schema Template - Updated for 6-Step UI (Yukon Government Design v2026.2)
const getPPPTemplate = () => ({
    type: 'PPP',
    version: '2026.2',
    // Step definitions for UI navigation
    steps: [
        { key: 'overview', title: 'Overview', sections: ['header', 'evaluation_period'] },
        { key: 'goals', title: 'Goals & Duties', sections: ['part1a'] },
        { key: 'development', title: 'Development', sections: ['part1b', 'part1c'] },
        { key: 'checkin', title: 'Check-in', sections: ['part2'] },
        { key: 'results', title: 'Results', sections: ['part3a', 'part3b', 'part3c', 'part3d'] },
        { key: 'summary', title: 'Summary', sections: ['summary', 'signatures'] }
    ],
    sections: {
        header: {
            title: 'Employee Information',
            description: 'Basic information about the employee',
            step: 'overview',
            owner: 'SYSTEM',
            fields: [
                { key: 'employee_name', type: 'text', label: 'Employee Name', source: 'HR', readonly: true },
                { key: 'employee_id', type: 'text', label: 'Employee ID', source: 'HR', readonly: true },
                { key: 'department', type: 'text', label: 'Department', source: 'HR', readonly: true },
                { key: 'position_title', type: 'text', label: 'Position Title', source: 'HR', readonly: true },
                { key: 'supervisor', type: 'user_select', label: 'Supervisor', source: 'HR', allow_override: true },
                { key: 'employment_tenure', type: 'select', label: 'Employment Tenure', options: ['Auxiliary', 'Permanent', 'Term'] }
            ]
        },
        evaluation_period: {
            title: 'Evaluation Period',
            description: 'Set the performance evaluation period',
            step: 'overview',
            owner: 'BOTH',
            fields: [
                { key: 'period_start', type: 'date', label: 'Start Date', required: true },
                { key: 'period_end', type: 'date', label: 'End Date', required: true },
                { key: 'purpose_of_report', type: 'toggle', label: 'Purpose of Report', options: ['Periodic', 'Probation', 'Other'] }
            ]
        },
        part1a: {
            title: 'Performance Duties and Goals',
            description: 'To be discussed and completed by the supervisor and the employee',
            step: 'goals',
            owner: 'BOTH',
            stage: 'PLANNING',
            fields: [
                { key: 'job_description_current', type: 'toggle', label: 'Is the job description up to date?', options: ['Yes', 'No'] },
                {
                    key: 'goals',
                    type: 'repeater',
                    label: 'Performance Goals',
                    min: 1,
                    max: 10,
                    item_schema: {
                        duty_objective: { type: 'textarea', label: 'Duty / Objective', placeholder: 'Describe the duty or objective...', required: true },
                        measure_of_success: { type: 'textarea', label: 'Measure of Success', placeholder: 'How will success be measured?' },
                        target_date: { type: 'date', label: 'Target Date' },
                        priority: { type: 'select', label: 'Priority', options: ['High', 'Medium', 'Low'] }
                    }
                }
            ]
        },
        part1b: {
            title: 'Career and Personal Development Plan',
            description: 'To be completed by the employee',
            step: 'development',
            owner: 'EMPLOYEE',
            stage: 'PLANNING',
            fields: [
                { key: 'career_development_plan', type: 'textarea', label: 'Career Development Plan', placeholder: 'Describe your career development goals and aspirations...', rows: 5 },
                { key: 'personal_development_plan', type: 'textarea', label: 'Personal Development Plan', placeholder: 'Describe your personal development goals...', rows: 5 },
                { key: 'training_needs', type: 'textarea', label: 'Training Needs', placeholder: 'List any training you feel you need...', rows: 3 }
            ]
        },
        part1c: {
            title: 'Training and Development Plans',
            description: 'To be completed by the supervisor',
            step: 'development',
            owner: 'SUPERVISOR',
            stage: 'PLANNING',
            fields: [
                { key: 'training_recommendations', type: 'textarea', label: 'Recommended Training', placeholder: 'Formal training, mentoring, coaching recommendations...', rows: 5 },
                { key: 'development_support', type: 'textarea', label: 'Development Support', placeholder: 'How will you support the employee\'s development?', rows: 3 },
                { key: 'checkin_date', type: 'date', label: 'Date scheduled for check-in' }
            ]
        },
        part2: {
            title: 'Mid-Point Check-in',
            description: 'Review progress and note any modifications',
            step: 'checkin',
            owner: 'BOTH',
            stage: 'INTERIM',
            fields: [
                { key: 'progress_summary', type: 'textarea', label: 'Progress Summary', placeholder: 'Summarize progress made towards goals...', rows: 5 },
                { key: 'goal_modifications', type: 'textarea', label: 'Goal Modifications', placeholder: 'List any modifications to goals and reasons...', rows: 4 },
                { key: 'challenges_faced', type: 'textarea', label: 'Challenges Faced', placeholder: 'Describe any challenges encountered...', rows: 3 },
                { key: 'support_needed', type: 'textarea', label: 'Support Needed', placeholder: 'What additional support is needed?', rows: 3 }
            ]
        },
        part3a: {
            title: 'Results Achieved',
            description: 'Year-end results review',
            step: 'results',
            owner: 'BOTH',
            stage: 'YEAR_END',
            fields: [
                { key: 'results_achieved', type: 'textarea', label: 'Results Achieved', placeholder: 'Document the results achieved for each goal...', rows: 6 },
                { key: 'factors_affecting_performance', type: 'textarea', label: 'Factors Affecting Performance', placeholder: 'List any factors that affected performance...', rows: 4 }
            ]
        },
        part3b: {
            title: 'Training & Development Taken',
            description: 'To be completed by the employee',
            step: 'results',
            owner: 'EMPLOYEE',
            stage: 'YEAR_END',
            fields: [
                { key: 'training_completed', type: 'textarea', label: 'Training Completed', placeholder: 'List all training completed during the review period...', rows: 4 },
                { key: 'certifications_earned', type: 'textarea', label: 'Certifications Earned', placeholder: 'List any certifications earned...', rows: 3 }
            ]
        },
        part3c: {
            title: 'Supervisor Support Provided',
            description: 'To be completed by the supervisor',
            step: 'results',
            owner: 'SUPERVISOR',
            stage: 'YEAR_END',
            fields: [
                { key: 'training_support_provided', type: 'textarea', label: 'Training Support Provided', placeholder: 'Describe the training support provided to the employee...', rows: 4 },
                { key: 'development_opportunities', type: 'textarea', label: 'Development Opportunities Given', placeholder: 'List development opportunities provided...', rows: 3 }
            ]
        },
        part3d: {
            title: 'Future Career Development',
            description: 'To be completed by the employee',
            step: 'results',
            owner: 'EMPLOYEE',
            stage: 'YEAR_END',
            fields: [
                { key: 'wants_career_discussion', type: 'toggle', label: 'Would you like to discuss future career development?', options: ['Yes', 'No'] },
                { key: 'career_aspirations', type: 'textarea', label: 'Career Aspirations', placeholder: 'Describe your career aspirations for the coming year...', rows: 4, conditional: { field: 'wants_career_discussion', value: 'Yes' } },
                { key: 'development_goals_next_year', type: 'textarea', label: 'Development Goals for Next Year', placeholder: 'What development goals do you have?', rows: 4, conditional: { field: 'wants_career_discussion', value: 'Yes' } }
            ]
        },
        summary: {
            title: 'Summary and Final Comments',
            description: 'Final comments, ratings, and response',
            step: 'summary',
            owner: 'BOTH',
            stage: 'YEAR_END',
            fields: [
                { key: 'supervisor_final_comments', type: 'textarea', label: 'Supervisor Final Comments', placeholder: 'Supervisor\'s final comments...', rows: 4, owner: 'SUPERVISOR' },
                { key: 'overall_rating', type: 'select', label: 'Overall Performance Rating', options: ['Exceeds Expectations', 'Meets Expectations', 'Needs Improvement', 'Unsatisfactory'], owner: 'SUPERVISOR' },
                { key: 'merit_recommendation', type: 'toggle', label: 'Merit Recommendation', options: ['Recommended', 'Not Recommended'], owner: 'SUPERVISOR' },
                { key: 'agreement_status', type: 'toggle', label: 'Do you agree with this assessment?', options: [{ value: 'agree', label: 'I Agree' }, { value: 'disagree', label: 'I Disagree' }], owner: 'EMPLOYEE' },
                { key: 'employee_final_comments', type: 'textarea', label: 'Employee Final Comments', placeholder: 'Employee\'s final comments...', rows: 4, owner: 'EMPLOYEE' }
            ]
        },
        signatures: {
            title: 'Digital Signatures',
            description: 'Final signatures to complete the performance plan',
            step: 'summary',
            owner: 'BOTH',
            stage: 'YEAR_END',
            fields: [
                { key: 'employee_signature_yearend', type: 'signature', label: 'Employee Signature', signer_role: 'EMPLOYEE' },
                { key: 'employee_signature_yearend_date', type: 'date', label: 'Date Signed', readonly: true, auto_fill: 'on_sign', owner: 'EMPLOYEE' },
                { key: 'supervisor_signature_yearend', type: 'signature', label: 'Supervisor Signature', signer_role: 'SUPERVISOR' },
                { key: 'supervisor_signature_yearend_date', type: 'date', label: 'Date Signed', readonly: true, auto_fill: 'on_sign', owner: 'SUPERVISOR' }
            ]
        }
    }
});

// PDP Form Schema Template (Manager version)
const getPDPTemplate = () => ({
    type: 'PDP',
    version: '2026.1',
    sections: {
        header: {
            title: 'Employee Information',
            description: 'Basic information about the manager/employee',
            owner: 'SYSTEM',
            fields: [
                { key: 'employee_name', type: 'text', label: 'Name', source: 'HR', readonly: true },
                { key: 'position_title', type: 'text', label: 'Position', source: 'HR', readonly: true },
                { key: 'supervisor', type: 'user_select', label: 'Supervisor', source: 'HR', allow_override: true },
                { key: 'period_start', type: 'date', label: 'Performance Period Start', required: true },
                { key: 'period_end', type: 'date', label: 'Performance Period End', required: true }
            ]
        },
        work_objectives: {
            title: 'Work Objectives',
            description: 'Define work objectives with success measures',
            owner: 'BOTH',
            stage: 'PLANNING',
            fields: [
                {
                    key: 'objectives',
                    type: 'repeater',
                    label: 'Work Objectives',
                    min: 1,
                    max: 5,
                    item_schema: {
                        objective: { type: 'textarea', label: 'Objective', required: true },
                        success_measures: { type: 'textarea', label: 'Success Measures' },
                        interim_results: { type: 'textarea', label: 'Interim Results', stage: 'INTERIM' },
                        yearend_results: { type: 'textarea', label: 'Year-End Results', stage: 'YEAR_END' }
                    }
                }
            ]
        },
        departmental_priorities: {
            title: 'Departmental Priorities',
            description: 'Standard departmental objectives',
            owner: 'BOTH',
            stage: 'PLANNING',
            fields: [
                { key: 'priority_customer_service', type: 'checkbox', label: 'Customer Service Excellence' },
                { key: 'priority_team_development', type: 'checkbox', label: 'Team Development' },
                { key: 'priority_process_improvement', type: 'checkbox', label: 'Process Improvement' },
                { key: 'priority_cost_management', type: 'checkbox', label: 'Cost Management' },
                { key: 'priority_innovation', type: 'checkbox', label: 'Innovation' },
                { key: 'departmental_notes', type: 'textarea', label: 'Notes on Departmental Priorities', rows: 3 }
            ]
        },
        training_development: {
            title: 'Training & Development',
            description: 'Development goals and learning activities',
            owner: 'EMPLOYEE',
            stage: 'PLANNING',
            fields: [
                { key: 'development_goals', type: 'textarea', label: 'Development Goals', placeholder: 'What skills or competencies do you want to develop?', rows: 4 },
                { key: 'learning_activities', type: 'textarea', label: 'Learning Activities', placeholder: 'What training, courses, or activities will you undertake?', rows: 4 },
                { key: 'expected_outcomes', type: 'textarea', label: 'Expected Outcomes', placeholder: 'What outcomes do you expect from these activities?', rows: 3 }
            ]
        },
        planning_signatures: {
            title: 'Planning Meeting Signatures',
            description: 'Signatures to confirm the planning meeting',
            owner: 'BOTH',
            stage: 'PLANNING',
            fields: [
                { key: 'planning_meeting_date', type: 'date', label: 'Planning Meeting Date' },
                { key: 'employee_signature_planning', type: 'signature', label: 'Employee Signature', signer_role: 'EMPLOYEE' },
                { key: 'supervisor_signature_planning', type: 'signature', label: 'Supervisor Signature', signer_role: 'SUPERVISOR' }
            ]
        },
        interim_review: {
            title: 'Interim Review',
            description: 'Mid-year progress review',
            owner: 'BOTH',
            stage: 'INTERIM',
            fields: [
                { key: 'interim_progress', type: 'textarea', label: 'Progress Summary', rows: 5 },
                { key: 'interim_challenges', type: 'textarea', label: 'Challenges and Support Needed', rows: 4 }
            ]
        },
        interim_signatures: {
            title: 'Interim Meeting Signatures',
            description: 'Signatures for the interim review',
            owner: 'BOTH',
            stage: 'INTERIM',
            fields: [
                { key: 'interim_meeting_date', type: 'date', label: 'Interim Meeting Date' },
                { key: 'employee_signature_interim', type: 'signature', label: 'Employee Signature', signer_role: 'EMPLOYEE' },
                { key: 'supervisor_signature_interim', type: 'signature', label: 'Supervisor Signature', signer_role: 'SUPERVISOR' }
            ]
        },
        performance_summary: {
            title: 'Performance Summary',
            description: 'Year-end performance summary',
            owner: 'BOTH',
            stage: 'YEAR_END',
            fields: [
                { key: 'employee_comments', type: 'textarea', label: 'Employee Comments', placeholder: 'Employee\'s reflection on the year...', rows: 5, owner: 'EMPLOYEE' },
                { key: 'supervisor_comments', type: 'textarea', label: 'Supervisor Comments', placeholder: 'Supervisor\'s assessment of performance...', rows: 5, owner: 'SUPERVISOR' },
                { key: 'overall_assessment', type: 'select', label: 'Overall Assessment', options: ['Outstanding', 'Exceeds Expectations', 'Meets Expectations', 'Needs Improvement', 'Unsatisfactory'], owner: 'SUPERVISOR' }
            ]
        },
        yearend_signatures: {
            title: 'Year-End Meeting Signatures',
            description: 'Final signatures to complete the PDP',
            owner: 'BOTH',
            stage: 'YEAR_END',
            fields: [
                { key: 'yearend_meeting_date', type: 'date', label: 'Year-End Meeting Date' },
                { key: 'employee_signature_yearend', type: 'signature', label: 'Employee Signature', signer_role: 'EMPLOYEE' },
                { key: 'supervisor_signature_yearend', type: 'signature', label: 'Supervisor Signature', signer_role: 'SUPERVISOR' }
            ]
        }
    }
});

// Default stages configuration
const getDefaultStages = () => ({
    PLANNING: {
        label: 'Planning',
        description: 'Set goals and objectives for the performance period',
        order: 1
    },
    INTERIM: {
        label: 'Interim Review',
        description: 'Mid-point check-in and progress review',
        order: 2
    },
    YEAR_END: {
        label: 'Year-End Review',
        description: 'Final review and assessment',
        order: 3
    },
    COMPLETED: {
        label: 'Completed',
        description: 'Performance plan has been completed and archived',
        order: 4
    }
});

export default {
    // Course-level
    saveUpdatePerformancePlan,

    // Configuration
    getConfig,
    saveConfig,

    // Plan instances
    createPlan,
    getPlan,
    savePlan,
    startPlan,

    // History
    getHistory,

    // Signatures
    sign,

    // Stage management
    submitStage,

    // Lists
    myPlans,
    supervisorPlans,
    listPlans,
    teamWithoutPlans,
    nudgeToStart,

    // Supervisor
    updateSupervisor,
    resolveSupervisorEmail,
    confirmSupervisor,
    searchUsers,
    requestSupervisorInput,
    removeSupervisor,

    // Templates
    getPPPTemplate,
    getPDPTemplate,
    getDefaultStages
};
